import React, { useState, useEffect, CSSProperties } from 'react';
import { Container } from 'modules/Sty';
import { CurrentBrand, BrandsToBeAddedToOrganization, OrganizationStatus } from 'modules/Types';
import { Body2 } from 'components/shareable/Typography';
import { BrandUser, getBrandUsers_api } from 'api/brandApi';
import Loader from 'elements/Loader';
import { Autocomplete } from '@mui/material';
import { TextField } from '@material-ui/core';
import AdminSearchSelectorModal from 'components/ProfileDropdown/AdminSearchSelectorModal';
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import Colors from 'modules/Colors';
import ButtonSymphony from 'elements/ButtonSymphony';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import InputSymphony from 'elements/InputSymphony';
import CardSymphony from 'elements/CardSymphony';

import useUser from 'Hooks/useUser';
import { useHistory } from 'react-router-dom';
import PageSymphony from 'elements/PageSymphony';
import UniversalUploadWidget from 'components/shareable/UniversalUploadWidget/UniversalUploadWidget';
import useUniversalUploadWidget from 'Hooks/useUniversalUploadWidget';
import Icon from 'elements/Icon';
import { TEAM_MAX_BRANDS_ALLOWED_DEFAULT, TEAM_MAX_USERS_ALLOWED_DEFAULT, TEAM_DEFAULT_PRICE_PER_BRAND_IN_DOLLARS, TEAM_DEFAULT_PRICE_PER_SEAT_IN_DOLLARS } from 'modules/Const';
import ProfilePictureSection from 'components/shareable/ProfilePictureSection';
import Banner from 'elements/Banner';
import useAppView from 'Hooks/useAppView';
import useScreen from 'Hooks/useScreen';


interface BrandSlot {
    id: number;
    name: string;
    image: string;
    slug: string;
    isSelected: boolean;
    isOccupied: boolean;
}

interface OrganizationNeedsSetupV2Props {
    title?: string;
}

const OrganizationNeedsSetupV2: React.FC<OrganizationNeedsSetupV2Props> = ({
    title,
}) => {
    const history = useHistory();
    const { mStyle } = useScreen()
    const { setAppView } = useAppView();
    const { loadedBrands, setCurrentBrand, updateOrAddBrand } = useBrand() as BrandExistsState;
    const { isSymphonyAdmin } = useUser();
    const { organization, getOrganization, completeOrganizationSetup, adminCreateTeamWithTrial } = useOrganizationV2();
    const { uploadedAssets, openUniversalUploadWidget, clearUploadedAssets } = useUniversalUploadWidget();

    // Determine if this is admin onboarding mode
    const isAdminOnboarding = isSymphonyAdmin;

    // Step management
    const [currentStep, setCurrentStep] = useState<number>(1);

    // Team name - prefilled with organization name
    const [teamName, setTeamName] = useState<string>(
        isAdminOnboarding ? '' : organization?.name || ''
    );

    // Admin setup options
    const [ownerEmail, setOwnerEmail] = useState<string>('');
    const [importedBrandUsersList, setImportedBrandsUsersList] = useState<BrandUser[]>([]);
    const [usersLoading, setUsersLoading] = useState<boolean>(false);
    const [openAdminSelector, setOpenAdminSelector] = useState<boolean>(false);

    // User invitation functionality
    const [userEmails, setUserEmails] = useState<string[]>(['']);
    const maxUsers = TEAM_MAX_USERS_ALLOWED_DEFAULT - 1; // Subtract 1 for current user

    // Brand slots based on max_brands_allowed (default to 3 for team plans)
    const [brandSlots, setBrandSlots] = useState<BrandSlot[]>([]);
    const maxBrandsAllowed = TEAM_MAX_BRANDS_ALLOWED_DEFAULT;

    // Loading state
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [profilePictureUrl, setProfilePictureUrl] = useState<string | undefined>();

    // Search state
    const [searchQuery, setSearchQuery] = useState<string>('');

    // Initialize brand slots
    const initializeBrandSlots = (): BrandSlot[] => {
        if (isAdminOnboarding) {
            // In admin mode, start with empty slots that can be filled via AdminSearchSelectorModal
            return [];
        }

        let slots: BrandSlot[] = [];

        if (loadedBrands?.length) {
            // Add existing brands to slots
            loadedBrands.forEach((brand: CurrentBrand, index: number) => {
                // if (index < maxBrandsAllowed) {
                slots.push({
                    id: brand.id,
                    name: brand.name!,
                    image: brand.image!,
                    slug: brand.slug!,
                    isSelected: true,
                    isOccupied: true
                });
                // }
            });
        }

        // Fill remaining slots as empty
        while (slots.length < maxBrandsAllowed) {
            slots.push({
                id: -slots.length - 1, // Negative ID for empty slots
                name: '',
                image: '',
                slug: '',
                isSelected: false,
                isOccupied: false
            });
        }

        return slots;
    };

    useEffect(() => {
        if (organization?.id && organization.setup_status === OrganizationStatus.READY) {
            setAppView('team', history);
        }
    }, [organization?.id, organization?.setup_status]);

    useEffect(() => {
        setBrandSlots(initializeBrandSlots());
    }, [loadedBrands, maxBrandsAllowed]);

    const getSelectedBrandsCount = (): number => {
        return brandSlots.filter(slot => slot.isSelected && slot.isOccupied).length;
    };

    const selectAllBrands = () => {
        setBrandSlots(prevSlots =>
            prevSlots.map(slot =>
                slot.isOccupied ? { ...slot, isSelected: true } : slot
            )
        );
    };

    const deselectAllBrands = () => {
        setBrandSlots(prevSlots =>
            prevSlots.map(slot =>
                slot.isOccupied ? { ...slot, isSelected: false } : slot
            )
        );
    };

    const getFilteredBrandSlots = () => {
        if (!searchQuery.trim()) {
            return brandSlots;
        }

        return brandSlots.filter(slot =>
            slot.isOccupied && slot.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const handleSlotToggle = (slotIndex: number) => {
        setBrandSlots(prevSlots => {
            const newSlots = [...prevSlots];
            if (newSlots[slotIndex].isOccupied) {
                newSlots[slotIndex].isSelected = !newSlots[slotIndex].isSelected;
            }
            return newSlots;
        });
    };

    const handleEmailChange = (index: number, value: string) => {
        setUserEmails(prev => {
            const newEmails = [...prev];
            newEmails[index] = value;
            return newEmails;
        });
    };

    const addEmailField = () => {
        setUserEmails(prev => [...prev, '']);
    };

    const removeEmailField = (index: number) => {
        if (userEmails.length > 1) {
            setUserEmails(prev => prev.filter((_, i) => i !== index));
        }
    };

    const getValidEmails = (): string[] => {
        return userEmails
            .map(email => email.trim())
            .filter(email => email && email.includes('@'));
    };

    const getImportedBrandUsers = async (brands: CurrentBrand[]) => {
        setUsersLoading(true);
        try {
            const userResponses = await Promise.all(
                brands.map(brand => getBrandUsers_api({ brandId: brand.id }))
            );

            const allUsers = userResponses.reduce((acc: BrandUser[], response) => {
                const brandUsers = response.users || [];
                return [
                    ...acc,
                    ...brandUsers.filter(newUser =>
                        !acc.some(existingUser => existingUser.email === newUser.email)
                    )
                ];
            }, []);

            setImportedBrandsUsersList(allUsers);
        } catch (error) {
            console.error('Error fetching brand users:', error);
        } finally {
            setUsersLoading(false);
        }
    };

    const handleAdminBrandImport = (brands: CurrentBrand[]) => {
        // Convert imported brands to brand slots
        const newSlots: BrandSlot[] = brands.map(brand => ({
            id: brand.id,
            name: brand.name!,
            image: brand.image!,
            slug: brand.slug!,
            isSelected: true,
            isOccupied: true
        }));

        setBrandSlots(newSlots);

        // Also fetch users from these brands for email suggestions
        getImportedBrandUsers(brands);
    };

    const handleNextStep = () => {
        setCurrentStep(2);
    };

    const handlePreviousStep = () => {
        setCurrentStep(1);
    };

    const handleFinishSetup = async () => {
        try {
            setIsLoading(true);

            const selectedBrandIds = brandSlots
                .filter(slot => slot.isSelected && slot.isOccupied)
                .map(slot => slot.id);

            const validEmails = getValidEmails();
            const profilePicture = profilePictureUrl || '';

            if (isAdminOnboarding) {
                // Use new admin team creation API
                const newOrganization = await adminCreateTeamWithTrial(
                    teamName,
                    ownerEmail,
                    selectedBrandIds,
                    validEmails,
                    profilePicture
                );

                if (newOrganization) {
                    // For admin setup, try to get brands from organization.brands if available
                    if (selectedBrandIds.length > 0) {
                        // Try to set the first brand as current brand for admin view
                        const firstBrandId = selectedBrandIds[0];
                        const { getBrand } = useBrand.getState();
                        const firstBrand = await getBrand(firstBrandId);
                        if (firstBrand) {
                            setCurrentBrand(firstBrand);
                        }
                    }

                    history.push('/team');
                }
            } else {
                // Call new complete organization setup with emails
                await completeOrganizationSetup(teamName, selectedBrandIds, validEmails, profilePicture);

                // Reload organization to show the updated view
                if (organization?.id) {
                    await getOrganization({ organizationId: organization.id });
                }
            }

            // Clear uploaded assets after successful setup
            clearUploadedAssets();
        } catch (error) {
            console.error("Setup error: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    const renderProfilePictureSection = () => {
        return (
            <ProfilePictureSection
                profilePictureUrl={profilePictureUrl}
                title="Team Profile Picture (Optional)"
                size={60}
                onProfilePictureUpdate={(url) => {
                    setProfilePictureUrl(url);
                }}
            />
        );
    };

    const renderAdminEmailSection = () => {
        if (!isSymphonyAdmin) return null;

        return (
            <Container className="adminEmailSection" display="flex" flexDirection="column" gap="8px">
                <InputSymphony
                    className="teamOwnerEmailInput"
                    label="Team Owner Email"
                    value={ownerEmail}
                    placeholder="Enter the team owner's email address"
                    onChange={(value) => setOwnerEmail(value)}
                    type="email"
                />
                {importedBrandUsersList.length > 0 && (
                    <Body2 style={{ fontSize: 14, color: Colors.greyDark }}>
                        Suggested from imported brands: {importedBrandUsersList.slice(0, 3).map(user => user.email).join(', ')}
                        {importedBrandUsersList.length > 3 && ` and ${importedBrandUsersList.length - 3} more`}
                    </Body2>
                )}
            </Container>
        );
    };

    // Helper component for rendering brand slot content
    const renderSlotContent = (slot: any, index: number) => {
        const isClickable = isAdminOnboarding || slot.isOccupied;
        const showCheckbox = slot.isOccupied || isAdminOnboarding;

        return (
            <div
                key={`slot-${index}`}
                className="brandSlot"
                style={{
                    ...sty.brandSlot,
                    backgroundColor: slot.isSelected ? '#f7eeff' : 'white',
                    opacity: isClickable ? 1 : 0.5
                }}
                onClick={() => isClickable && handleSlotToggle(index)}
            >
                <Container display="flex" alignItems="center" gap="12px">
                    {slot.isOccupied || isAdminOnboarding ? (
                        <>
                            <div className="brandAvatar" style={{ ...sty.brandAvatar, backgroundImage: slot.image ? `url(${slot.image})` : 'none' }}>
                                {!slot.image && <Icon className="userIcon" icon="user" size={20} />}
                            </div>
                            <div className="slotInfo" style={sty.slotInfo}>
                                <div style={sty.slotName}>{slot.name}</div>
                                <div style={sty.slotStatus}>
                                    {slot.isSelected ? 'Selected for team' : 'Click to add'}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={sty.emptySlot}>
                                <Icon className="plusIcon" icon="plus" size={20} />
                            </div>
                            <div style={sty.slotInfo}>
                                <div style={sty.slotName}>Empty Slot</div>
                                <div style={sty.slotStatus}>Available for future profiles</div>
                            </div>
                        </>
                    )}
                </Container>

                {showCheckbox && (
                    <div
                        className="slotCheckbox"
                        style={{
                            ...sty.slotCheckbox,
                            backgroundColor: slot.isSelected ? Colors.purple : 'white'
                        }}
                    >
                        {slot.isSelected && <Icon className="checkIcon" icon="check" size={12} color="white" />}
                    </div>
                )}
            </div>
        );
    };

    const renderBrandSlots = () => {
        const title = isAdminOnboarding
            ? 'Select profiles to add to the team (Optional)'
            : 'Select the profiles you want to add to your Team';

        const occupiedSlots = brandSlots.filter(slot => slot.isOccupied);
        const filteredSlots = getFilteredBrandSlots();
        const showSearch = occupiedSlots.length > 3;
        const showActionButtons = occupiedSlots.length > 0;

        return (
            <Container className="brandSlotsSection" display="flex" flexDirection="column" gap="12px">
                <Body2 style={{ fontWeight: 500 }}>
                    {title}
                </Body2>

                {isAdminOnboarding && (
                    <Body2 style={{ fontSize: 14, color: Colors.greyDark }}>
                        You can create the team without any profiles and add them later.
                    </Body2>
                )}

                {/* Search Bar - show if more than 3 brands */}
                {showSearch && (
                    <InputSymphony
                        className="brandSearchInput"
                        placeholder="Search profiles..."
                        value={searchQuery}
                        onChange={(value) => setSearchQuery(value)}
                        isSearch
                    />
                )}

                {/* Action Buttons */}
                {showActionButtons && (
                    <Container className="actionButtons" display="flex" gap="8px">
                        <ButtonSymphony
                            className="selectAllButton"
                            variant="outlined"
                            size="small"
                            onClick={selectAllBrands}
                            style={sty.actionButton}
                        >
                            Select All
                        </ButtonSymphony>
                        <ButtonSymphony
                            size="small"
                            className="deselectAllButton"
                            variant="outlined"
                            onClick={deselectAllBrands}
                            style={sty.actionButton}
                        >
                            Deselect All
                        </ButtonSymphony>
                    </Container>
                )}

                {isAdminOnboarding && brandSlots.length === 0 ? (
                    <div style={sty.emptyBrandsMessage}>
                        <Icon className="searchIcon" icon="search" size={24} color={Colors.greyDark} />
                        <Body2 style={{ color: Colors.greyDark, textAlign: 'center' }}>
                            No profiles selected. Use the button below to search and import profiles.
                        </Body2>
                    </div>
                ) : (
                    <div className="brandSlotsList" style={sty.brandSlotsList}>
                        {filteredSlots.map((slot, index) => {
                            // Find the original index in brandSlots for proper handling
                            const originalIndex = brandSlots.findIndex(originalSlot => originalSlot.id === slot.id);
                            return renderSlotContent(slot, originalIndex);
                        })}
                    </div>
                )}

                {/* Show message if search has no results */}
                {showSearch && searchQuery.trim() && filteredSlots.length === 0 && (
                    <Body2 style={{ color: Colors.greyDark, textAlign: 'center', padding: 16 }}>
                        No profiles match your search.
                    </Body2>
                )}

                {isAdminOnboarding && (
                    <ButtonSymphony
                        className="importFromAdminSearchButton"
                        variant="outlined"
                        onClick={() => setOpenAdminSelector(true)}
                    >
                        {brandSlots.length > 0 ? 'Import Additional Profiles' : 'Search & Import Profiles'}
                    </ButtonSymphony>
                )}
            </Container>
        );
    };

    const renderCountSection = (
        type: 'brands' | 'users',
        selectedCount: number,
        maxAllowed: number,
        pricePerItem: number,
        className: string
    ) => {
        const totalSeatsRemaining = maxAllowed - selectedCount;
        const isOverLimit = selectedCount > maxAllowed;
        const overageCount = Math.max(0, selectedCount - maxAllowed);

        let mainText = '';
        let subText = '';

        const itemName = type === 'brands' ? 'brand' : 'team member';
        const itemNamePlural = type === 'brands' ? 'brands' : 'team members';
        const slotName = type === 'brands' ? 'profile' : 'team member';
        const slotNamePlural = type === 'brands' ? 'profiles' : 'team members';

        if (isOverLimit) {
            mainText = `You have ${maxAllowed} ${slotName} ${maxAllowed === 1 ? 'slot' : 'slots'} included in your Team plan. Additional ${slotNamePlural} are charged at $${pricePerItem} per month.`;
            subText = `You currently have ${overageCount} additional ${overageCount === 1 ? itemName : itemNamePlural} selected, which will incur an extra $${overageCount * pricePerItem} per month.`;
        } else {
            mainText = `You have ${totalSeatsRemaining} more ${slotName} ${totalSeatsRemaining === 1 ? 'slot' : 'slots'} available in your Team plan.`;
            subText = `You can add more ${slotNamePlural} to your team at any time.`;
        }

        return (
            <div className={className} style={sty.selectedCountSection}>
                <div className="countInfo" style={sty.countInfo}>
                    <p style={sty.countMainText}>
                        {mainText}
                    </p>
                    <p style={sty.countSubText}>
                        {subText}
                    </p>
                </div>
            </div>
        );
    };

    const renderSelectedArtistCountSection = () => {
        const selectedCount = getSelectedBrandsCount();
        return renderCountSection(
            'brands',
            selectedCount,
            maxBrandsAllowed,
            TEAM_DEFAULT_PRICE_PER_BRAND_IN_DOLLARS,
            'selectedArtistCountSection'
        );
    };

    const renderSelectedUserCountSection = () => {
        const selectedUserCount = getValidEmails().length;
        return renderCountSection(
            'users',
            selectedUserCount,
            maxUsers,
            TEAM_DEFAULT_PRICE_PER_SEAT_IN_DOLLARS,
            'selectedUserCountSection'
        );
    };

    const renderBrandSelection = () => {
        return (
            <>
                {/* Team Name Input */}
                <InputSymphony
                    className="teamNameInput"
                    label="What do you want to call your team?"
                    value={teamName}
                    placeholder="Enter your team's name"
                    onChange={(value) => setTeamName(value)}
                    maxLength={90}
                />

                {/* Profile Picture Upload */}
                {renderProfilePictureSection()}

                {/* Admin Email Section */}
                {renderAdminEmailSection()}

                {/* Brand Slots */}
                {renderBrandSlots()}

                {/* Selected Count Section */}
                {renderSelectedArtistCountSection()}

                {/* Next Button */}
                <ButtonSymphony
                    className="nextStepButton"
                    onClick={handleNextStep}
                    disabled={!canProceedToStep2()}
                    style={sty.nextButton}
                >
                    Next
                </ButtonSymphony>
            </>
        );
    };

    const renderUserInvites = () => {
        return (
            <>
                <Container display="flex" flexDirection="column" gap="16px">
                    <Body2 style={{ fontSize: 16, fontWeight: 500 }}>
                        Invite team members to collaborate on your profiles.
                    </Body2>

                    {/* User Invitation Section */}
                    {renderUserInvitationSection()}

                    {/* User Count Section */}
                    {renderSelectedUserCountSection()}

                    {/* Action Buttons */}
                    <Container display="flex" gap="12px" style={{ alignSelf: 'center', marginTop: 16 }}>
                        <ButtonSymphony
                            className="backButton"
                            variant="outlined"
                            onClick={handlePreviousStep}
                            style={sty.backButton}
                        >
                            Back
                        </ButtonSymphony>

                        <ButtonSymphony
                            className="finishSetupButton"
                            onClick={handleFinishSetup}
                            disabled={!canFinishSetup()}
                            isLoading={isLoading}
                            style={sty.finishButton}
                        >
                            Finish Setup
                        </ButtonSymphony>
                    </Container>
                </Container>
            </>
        );
    };

    const renderUserInvitationSection = () => {
        return (
            <Container className="userInvitationSection" display="flex" flexDirection="column" gap="16px">

                <div className="emailInputsList" style={sty.emailInputsList}>
                    {userEmails.map((email, index) => (
                        <Container key={index} display="flex" alignItems="center" gap="8px">
                            <InputSymphony
                                className={`emailInput-${index}`}
                                value={email}
                                placeholder="Enter email address"
                                onChange={(value) => handleEmailChange(index, value)}
                                type="email"
                                style={{ flex: 1 }}
                            />
                            {userEmails.length > 1 && (
                                <ButtonSymphony
                                    className={`removeEmailButton-${index}`}
                                    variant="text"
                                    size="small"
                                    onClick={() => removeEmailField(index)}
                                    style={{ minWidth: 'auto', padding: '4px 8px' }}
                                >
                                    <Icon className="removeIcon" icon="close" size={16} />
                                </ButtonSymphony>
                            )}
                        </Container>
                    ))}
                </div>

                <ButtonSymphony
                    className="addEmailButton"
                    variant="text"
                    onClick={addEmailField}
                    style={{ alignSelf: 'flex-start' }}
                >
                    + Add another team member
                </ButtonSymphony>

            </Container>
        );
    };

    const canProceedToStep2 = () => {
        const hasValidTeamName = teamName && teamName.trim().length >= 3;
        const hasSelectedBrands = getSelectedBrandsCount() > 0;
        return hasValidTeamName && hasSelectedBrands;
    };

    const canFinishSetup = () => {
        return canProceedToStep2() && !isLoading;
    };

    return (
        <PageSymphony className="organizationNeedsSetupV2" isContent>
            <UniversalUploadWidget />

            <Container
                className="setupContainer"
                display="flex"
                flexDirection="column"
                gap="24px"
                style={mStyle(sty, "setupContainer")}
            >
                <CardSymphony
                    className="setupHeader"
                    title={title || `Setup your Team - Step ${currentStep} of 2`}
                    style={sty.setupCard}
                >
                    <Container display="flex" flexDirection="column" gap="24px">
                        {currentStep === 1 ? renderBrandSelection() : renderUserInvites()}
                    </Container>
                </CardSymphony>

                {/* Admin Search Modal */}
                {isSymphonyAdmin && (
                    <AdminSearchSelectorModal
                        mode="select"
                        onImportSelected={(brands) => {
                            handleAdminBrandImport(brands as CurrentBrand[]);
                        }}
                        open={openAdminSelector}
                        onClose={() => setOpenAdminSelector(false)}
                    />
                )}
            </Container>
        </PageSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    setupContainer: {
        maxWidth: 600,
        width: 600,
        margin: '0 auto',
        padding: 24
    },
    setupContainer_mobile: {
        width: '100%'
    },
    setupCard: {
        padding: 32
    },
    actionButton: {
        fontSize: 14,
        padding: '6px 12px',
        minWidth: 'auto'
    },

    brandSlotsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxHeight: 300,
        overflowY: 'auto'
    },
    brandSlot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 16,
        border: `1px solid ${Colors.greyLight}`,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.2s ease'
    },
    brandAvatar: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: Colors.greyLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `2px solid ${Colors.grey}`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
    },
    emptySlot: {
        width: 40,
        height: 40,
        borderRadius: '50%',
        backgroundColor: Colors.greyLight,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `2px dashed ${Colors.grey}`
    },
    slotInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    slotName: {
        fontSize: 16,
        fontWeight: 500,
        color: Colors.black
    },
    slotStatus: {
        fontSize: 14,
        color: Colors.greyDark
    },
    slotCheckbox: {
        width: 20,
        height: 20,
        borderRadius: 4,
        border: `2px solid ${Colors.grey}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    nextButton: {
        alignSelf: 'center',
        minWidth: 200
    },
    backButton: {
        minWidth: 120
    },
    finishButton: {
        minWidth: 200
    },
    emailInputsList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12
    },
    emailPreview: {
        padding: 12,
        backgroundColor: '#f7eeff',
        borderRadius: 8,
        border: `1px solid ${Colors.purple}20`
    },
    selectedCountSection: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 16px',
        backgroundColor: '#F0F0F0',
        borderRadius: 8,
        flexDirection: 'row',
        justifyContent: 'space-between',
        margin: '8px 0'
    },
    countInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: 4
    },
    countMainText: {
        fontSize: 16,
        fontWeight: 500,
        color: Colors.greyDark,
        margin: 0
    },
    countSubText: {
        fontSize: 14,
        color: Colors.black,
        margin: 0
    },
    emptyBrandsMessage: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: 32,
        border: `2px dashed ${Colors.grey}`,
        borderRadius: 8,
        backgroundColor: Colors.greyLight
    }
};

export default OrganizationNeedsSetupV2;